import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMoneyCheckAlt, FaFilePdf, FaLock, FaLockOpen } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Commissionreport.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://uat.definiteassurance.com/api';
const COMPANY = 'Definite';
const ROWS_PER_PAGE = 20;
const SMS_WEBHOOK = 'https://sms.definiteassurance.com/webhook/d649cb06-3ad4-4298-ae27-e9b14f32d809';

// ══════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════
const formatISODate = (input) => {
  try {
    if (!input) return '--';
    if (typeof input === 'string') return input.split('T')[0];
    if (input instanceof Date) return input.toISOString().split('T')[0];
    return '--';
  } catch { return '--'; }
};

const toMs = (input) => {
  if (!input) return 0;
  if (typeof input === 'string') return new Date(input).getTime();
  if (input instanceof Date) return input.getTime();
  return 0;
};

const formatKES = (amount) => {
  const n = parseFloat(amount);
  if (isNaN(n)) return '0.00';
  return n.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
};

const monthStartMs = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).getTime();
};

const normalisePhone = (raw = '') => {
  const s = String(raw).replace(/\s+/g, '');
  if (s.startsWith('+254')) return s.substring(1);
  if (s.startsWith('254')) return s;
  if (s.startsWith('07')) return '254' + s.substring(1);
  if (s.startsWith('7')) return '254' + s;
  return s;
};

const buildPageNumbers = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  const s = Math.max(2, current - 1);
  const e = Math.min(total - 1, current + 1);
  if (s > 2) pages.push('...');
  for (let i = s; i <= e; i++) pages.push(i);
  if (e < total - 1) pages.push('...');
  pages.push(total);
  return pages;
};

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function Commissionreport() {
  const navigate = useNavigate();

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsauth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentcode] = useState('');
  const [agentName, setAgentname] = useState('');
  const [phone, setPhone] = useState('');

  // ── Data ──
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  // ── Date filter ──
  const [startMs, setStartMs] = useState(null);
  const [endMs, setEndMs] = useState(null);
  const [monthName, setMonthname] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // ── Commission unlock ──
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [modal, setModal] = useState(false);
  const [secretCode, setSecretCode] = useState(null);
  const [enteredCode, setEnteredCode] = useState('');
  const [fetchingCode, setFetchingCode] = useState(false);
  const [resetting, setResetting] = useState(false);

  // ── PDF export ──
  const [exporting, setExporting] = useState(false);

  // ══════════════════════════════════════
  //  AUTH HEADERS
  // ══════════════════════════════════════
  const authHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    ...(credentials ? { Authorization: `Basic ${credentials}` } : {}),
  }), [credentials]);

  // ══════════════════════════════════════
  //  CREDENTIALS
  // ══════════════════════════════════════
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/getCredentials?username=${COMPANY}`);
        const data = await res.json();
        setCredentials(btoa(`${data.Username}:${data.Password}`));
        setIsauth(true);
      } catch {
        toast.error('Failed to initialise authentication.');
      }
    })();
  }, []);

  // ══════════════════════════════════════
  //  AGENT FROM LOCAL STORAGE
  // ══════════════════════════════════════
  useEffect(() => {
    const user = localStorage.getItem('definiteagent');
    if (user) {
      setAgent(user);
    } else {
      navigate('/login');
    }
    setMonthname(new Date().toLocaleString('default', { month: 'long' }));
  }, [navigate]);

  // ══════════════════════════════════════
  //  AGENT CODE + PHONE
  // ══════════════════════════════════════
  useEffect(() => {
    if (!agent || !isAuth) return;
    (async () => {
      try {
        const res = await fetch(`${API}/getDealerdetails?email=${agent}`, { headers: authHeaders() });
        const data = await res.json();
        setAgentcode(data.OID);
        setAgentname(data.Dealer_name || '');
        setPhone(normalisePhone(data.Mobile));
      } catch {
        toast.error('Failed to load agent details.');
      }
    })();
  }, [agent, isAuth, authHeaders]);

  // ══════════════════════════════════════
  //  FETCH COMMISSION DATA
  // ══════════════════════════════════════
  const fetchCommissions = useCallback(async () => {
    if (!agentCode || !isAuth) return;
    setLoading(true);
    setFetchError('');
    try {
      const res = await fetch(`${API}/getCommissions?agent=${agentCode}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Server error');
      const data = await res.json();
      setCommissions(Array.isArray(data) ? data : []);
    } catch {
      setFetchError('Failed to load commission data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [agentCode, isAuth, authHeaders]);

  useEffect(() => { fetchCommissions(); }, [fetchCommissions]);

  // ══════════════════════════════════════
  //  DATE FILTER HANDLERS
  // ══════════════════════════════════════
  const handleStartDate = (e) => {
    const ms = new Date(e.target.value).getTime();
    setStartMs(ms);
    setMonthname(new Date(ms).toLocaleString('default', { month: 'long' }));
    setCurrentPage(1);
  };

  const handleEndDate = (e) => {
    setEndMs(new Date(e.target.value).getTime());
    setCurrentPage(1);
  };

  // ══════════════════════════════════════
  //  FILTERED DATA (client-side)
  // ══════════════════════════════════════
  const filtered = useMemo(() => {
    const defaultStart = monthStartMs();
    return commissions.filter(c => {
      const ms = toMs(c.DateDone);
      if (startMs !== null && endMs !== null) return ms >= startMs && ms <= endMs;
      if (startMs !== null) return ms >= startMs;
      return ms >= defaultStart;
    });
  }, [commissions, startMs, endMs]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const indexOfFirst = (currentPage - 1) * ROWS_PER_PAGE;
  const currentRows = useMemo(
    () => filtered.slice(indexOfFirst, indexOfFirst + ROWS_PER_PAGE),
    [filtered, indexOfFirst]
  );
  const pageNumbers = useMemo(() => buildPageNumbers(currentPage, totalPages), [currentPage, totalPages]);

  // ── Summary totals ──
  const totals = useMemo(() => ({
    grossPremium: filtered.reduce((s, c) => s + (c.GrossPremium || 0), 0),
    paidAmount: filtered.reduce((s, c) => s + (c.PaidAmount || 0), 0),
    netCommission: filtered.reduce((s, c) => s + (c.NetCommission || 0), 0),
    count: filtered.length,
  }), [filtered]);

  useEffect(() => { setCurrentPage(1); }, [startMs, endMs]);

  // ══════════════════════════════════════
  //  PDF EXPORT (jsPDF autotable)
  // ══════════════════════════════════════
  const exportPDF = useCallback(() => {
    setExporting(true);
    try {
      const doc = new jsPDF('l', 'mm', 'a4');
      doc.setFontSize(14);
      doc.setTextColor(44, 62, 80);
      doc.text(
        `${monthName.toUpperCase()} COMMISSION STATEMENT`,
        doc.internal.pageSize.getWidth() / 2,
        14,
        { align: 'center' }
      );

      const head = [['#', 'Note No.', 'Date Done', 'Registration', 'Gross Premium', 'Premium Paid', ...(isUnlocked ? ['Net Commission'] : [])]];
      const body = filtered.map((c, i) => [
        i + 1,
        c.DocumentNo ?? '--',
        formatISODate(c.DateDone),
        c.Narration ?? '--',
        formatKES(c.GrossPremium),
        formatKES(c.PaidAmount),
        ...(isUnlocked ? [formatKES(c.NetCommission)] : []),
      ]);

      doc.autoTable({
        head,
        body,
        startY: 20,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [26, 92, 90], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 250, 249] },
        foot: [[
          '', '', '', 'TOTALS',
          formatKES(totals.grossPremium),
          formatKES(totals.paidAmount),
          ...(isUnlocked ? [formatKES(totals.netCommission)] : []),
        ]],
        footStyles: { fillColor: [240, 247, 246], textColor: [26, 92, 90], fontStyle: 'bold' },
      });

      doc.save(`commission_statement_${monthName.toLowerCase()}.pdf`);
    } catch {
      toast.error('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [filtered, isUnlocked, monthName, totals]);

  // ══════════════════════════════════════
  //  VIEW COMMISSIONS (fetch & send code)
  // ══════════════════════════════════════
  const openCommissionModal = async () => {
    if (isUnlocked) {
      setIsUnlocked(false);   // re-lock
      return;
    }
    setFetchingCode(true);
    try {
      const res = await fetch(`${API}/getGeneratedcode?agent=${agentCode}`, { headers: authHeaders() });
      const data = await res.json();
      setSecretCode(data.code);
      if (data.new && phone) {
        await axios.post(SMS_WEBHOOK, {
          MessageParameters: [{ Number: [phone], Text: `${data.code}` }],
        });
        toast.success('Verification code sent to your phone.');
      }
    } catch {
      toast.error('Failed to fetch verification code.');
    } finally {
      setFetchingCode(false);
      setEnteredCode('');
      setModal(true);
    }
  };

  // ══════════════════════════════════════
  //  RESET CODE
  // ══════════════════════════════════════
  const resetCode = async () => {
    setResetting(true);
    try {
      const res = await fetch(`${API}/Resetcode?agent=${agentCode}`, { headers: authHeaders() });
      const data = await res.json();
      setSecretCode(data.code);
      if (data.new && phone) {
        await axios.post(SMS_WEBHOOK, {
          MessageParameters: [{ Number: [phone], Text: `${data.code}` }],
        });
        toast.success('New code sent to your phone.');
      }
    } catch {
      toast.error('Failed to reset code.');
    } finally {
      setResetting(false);
    }
  };

  // ══════════════════════════════════════
  //  VERIFY CODE
  // ══════════════════════════════════════
  const verifyCode = () => {
    if (String(enteredCode).trim() === String(secretCode).trim()) {
      setIsUnlocked(true);
      setModal(false);
      toast.success('Commissions unlocked!');
    } else {
      toast.error('Incorrect code. Please try again.');
    }
  };

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="comm-page">
      <ToastContainer position="top-right" autoClose={4000} />
      <Header userName={agentName || agent} />
      <Sidebar />

      <main className="comm-main">
        {/* Breadcrumb */}
        <div className="comm-top-row">
          <span className="comm-company">Definite Assurance</span>
          <span className="comm-breadcrumb">Dashboard &rsaquo; Commission Report</span>
        </div>

        <div className="comm-card">
          <div className="comm-card-header">
            <h3><FaMoneyCheckAlt /> Commission Statement — {monthName}</h3>
          </div>

          {/* Toolbar */}
          <div className="comm-toolbar">
            <div className="comm-date-group">
              <label>From Date</label>
              <input type="date" onChange={handleStartDate} />
            </div>
            <div className="comm-date-group">
              <label>To Date</label>
              <input type="date" onChange={handleEndDate} />
            </div>
            <div className="comm-toolbar-actions">
              <button
                className="comm-export-btn"
                onClick={exportPDF}
                disabled={exporting || filtered.length === 0}
              >
                <FaFilePdf /> {exporting ? 'Exporting…' : 'Export PDF'}
              </button>
              <button
                className="comm-unlock-btn"
                onClick={openCommissionModal}
                disabled={fetchingCode}
              >
                {isUnlocked
                  ? <><FaLockOpen /> Hide Commissions</>
                  : <><FaLock /> {fetchingCode ? 'Please wait…' : 'View Commissions'}</>
                }
              </button>
            </div>
          </div>

          {/* Summary strip */}
          {!loading && filtered.length > 0 && (
            <div className="comm-summary">
              <div className="comm-summary-item">
                <span>Records</span>
                <span>{totals.count}</span>
              </div>
              <div className="comm-summary-item">
                <span>Gross Premium</span>
                <span>KES {formatKES(totals.grossPremium)}</span>
              </div>
              <div className="comm-summary-item">
                <span>Premium Paid</span>
                <span>KES {formatKES(totals.paidAmount)}</span>
              </div>
              <div className={`comm-summary-item${isUnlocked ? '' : ' locked'}`}>
                <span>Net Commission</span>
                <span>{isUnlocked ? `KES ${formatKES(totals.netCommission)}` : '••••••'}</span>
              </div>
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div className="comm-loading">
              <div className="comm-spinner" />
              <p>Loading commission data…</p>
            </div>
          ) : fetchError ? (
            <div className="comm-error-state">
              <p>{fetchError}</p>
              <button onClick={fetchCommissions}>Retry</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="comm-empty">
              No commission records found for the selected period.
            </div>
          ) : (
            <>
              <div className="comm-table-wrap">
                <table className="comm-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Note No.</th>
                      <th>Date Done</th>
                      <th>Registration</th>
                      <th>Gross Premium</th>
                      <th>Premium Paid</th>
                      <th>Net Commission (KES)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.map((val, idx) => (
                      <tr key={val.OID ?? idx}>
                        <td>{indexOfFirst + idx + 1}</td>
                        <td>{val.DocumentNo ?? '--'}</td>
                        <td>{formatISODate(val.DateDone)}</td>
                        <td>{val.Narration ?? '--'}</td>
                        <td>{formatKES(val.GrossPremium)}</td>
                        <td>{formatKES(val.PaidAmount)}</td>
                        <td>
                          {isUnlocked
                            ? formatKES(val.NetCommission)
                            : <span className="comm-hidden">••••••</span>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'right' }}>Totals</td>
                      <td>KES {formatKES(totals.grossPremium)}</td>
                      <td>KES {formatKES(totals.paidAmount)}</td>
                      <td>{isUnlocked ? `KES ${formatKES(totals.netCommission)}` : '——'}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Pagination */}
              <div className="comm-pagination">
                <span className="comm-pagination-info">
                  Showing {indexOfFirst + 1}–{Math.min(indexOfFirst + ROWS_PER_PAGE, filtered.length)} of {filtered.length}
                </span>
                <div className="comm-pagination-controls">
                  <button
                    className="comm-page-btn"
                    onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ‹ Prev
                  </button>
                  {pageNumbers.map((p, i) =>
                    p === '...'
                      ? <span key={`e${i}`} className="comm-page-ellipsis">…</span>
                      : <button
                          key={p}
                          className={`comm-page-btn${currentPage === p ? ' active' : ''}`}
                          onClick={() => setCurrentPage(p)}
                        >
                          {p}
                        </button>
                  )}
                  <button
                    className="comm-page-btn"
                    onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next ›
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* ── Commission Verify Modal ── */}
      {modal && (
        <div className="comm-modal-overlay">
          <div className="comm-modal">
            <div className="comm-modal-header">
              <h4><FaLock style={{ marginRight: 6 }} />View Commissions</h4>
              <button className="comm-modal-close" onClick={() => setModal(false)} aria-label="Close">&times;</button>
            </div>
            <p>Enter the secret code sent to your registered phone number to unlock commission figures.</p>

            <div className="comm-form-group">
              <label>Secret Code</label>
              <input
                type="text"
                placeholder="Enter code…"
                value={enteredCode}
                onChange={e => setEnteredCode(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && verifyCode()}
                autoFocus
              />
            </div>

            <button className="comm-verify-btn" onClick={verifyCode}>
              Verify &amp; Unlock
            </button>

            <button
              className="comm-reset-link"
              onClick={resetCode}
              disabled={resetting}
            >
              {resetting ? 'Resending…' : 'Reset / resend code'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
